import React, { useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Spinner,
  Center,
  Button,
  useToast,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { EditIcon, CloseIcon } from '@chakra-ui/icons';
import { Formik, Form } from 'formik';
import moment from 'moment';
import BullionDeskAPI from 'api/bulliondesk';
import OperationLocationAPI from 'api/operationlocation';
import ClientConfigAPI from 'api/clientconfig';
import Card from 'components/card/Card';
import SelectField from 'components/formComponents/selectField.js';
import InputField from 'components/formComponents/inputField.js';
import { MetalTypesOptions } from 'config/constant';
import { useHistory } from 'react-router-dom';
import { AddBullionDeskValidationSchema } from 'formValidations/addBullionDeskFormValidations';

export default function AddBullionDesk(props) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState();
  const [filterEndDate, setFilterEndDate] = useState();
  const today = new Date().toISOString().split('T')[0];
  const [operationList, setOperationList] = useState([]);
  const toast = useToast();
  const toastIdRef = React.useRef();
  const [disabled, setDisabled] = useState(true);
  const [inpDisable, setInpDisable] = useState(true);
  const [bullionDeskData, setBullionDeskData] = useState({});
  const [goldBuySellPriceGap, setGoldBuySellPriceGap] = useState(null);
  const enable = () => setDisabled(!disabled);
  const disable = () => setDisabled(true);

  const FetchOperationLocationList = async () => {
    try {
      let response = await OperationLocationAPI.List({
        currentPage: 1,
        itemsPerPage: 100,
        sort: 'name:asc',
        booleanFilter: true,
      });
      response = response.data;
      if (response.status_code === 200 && response?.data?.list?.length > 0) {
        setOperationList(response?.data?.list);
      } else {
        setOperationList([]);
      }
    } catch (err) {
      console.log(err);
      setOperationList([]);
    }
  };

  const FetchClientConfigData = async () => {
    try {
      let response = await ClientConfigAPI.Get();
      if (response.data.status_code === 200 && response?.data?.data?.length > 0) {
        setGoldBuySellPriceGap(response.data.data[0].gold_buy_sell_price_gap);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    FetchOperationLocationList();
    FetchClientConfigData();
  }, []);

  const handleSubmit = async (values, actions) => {
    console.log('handle submit called');

    if (values.type === 'GOLD22KT' && goldBuySellPriceGap !== null) {
      const buyPrice = Number(values.buy);
      const sellPrice = Number(values.sell);
      const gap = Number(goldBuySellPriceGap);
      const maxSell = Math.round(buyPrice - (buyPrice * gap / 100));
      if (sellPrice > maxSell) {
        toast({
          title: `Sell price cannot exceed maximum allowed limit (${maxSell}) based on ${gap}% gap interval for Gold`,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
        return;
      }
    }

    try {
      const api = await BullionDeskAPI.Create({
        ...values,
        buy: Number(values.buy),
        sell: Number(values.sell),
        // exchange: Number(values.exchange),
        threshold_sell: Number(values.threshold_sell),
        threshold_buy: Number(values.threshold_buy),
        threshold_sell_addon: Number(values.threshold_sell_addon),
        threshold_buy_addon: Number(values.threshold_buy_addon),
      });
      console.log(api);
      console.log('data is here-----', api.data);
      if (api.data.status_code === 200) {
        toast({
          title: api.data.message || 'Successfully Added',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        actions.resetForm();
        history.push('/admin/bullion-desks');
      } else {
        toast({
          title: api.data.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      console.log(err);
      toast({
        title: 'Error Adding',
        status: 'error',
        duration: 1000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const initialValues = {
    unit: 'gm',
    type: 'GOLD22KT',
    start: '',
    expiry: '',
    operationLocationId: '',
    buy: '',
    sell: '',
    // exchange: '',
    threshold_sell: '',
    threshold_buy: '',
    threshold_sell_addon: '',
    threshold_buy_addon: '',
  };

  return loading ? (
    <Center
      mt='10rem'
      width='100%'
    // bgColor='red'
    >
      <Spinner size='xl' />
    </Center>
  ) : (
    <Flex
      maxWidth='7xl'
      flexDirection='column'
      gap={8}
      mt='10rem'>
      {/* <Text
        fontSize='3xl'
        fontWeight='400'>
        Bullion Desk for
      </Text> */}

      <Formik
        initialValues={initialValues}
        validationSchema={AddBullionDeskValidationSchema}
        validate={(values) => {
          const errors = {};
          if (values.type === 'GOLD22KT' && goldBuySellPriceGap !== null && values.buy && values.sell) {
            const buyPrice = Number(values.buy);
            const sellPrice = Number(values.sell);
            const gap = Number(goldBuySellPriceGap);
            const maxSell = Math.round(buyPrice - (buyPrice * gap / 100));
            if (sellPrice > maxSell) {
              errors.sell = `Sell price cannot exceed maximum allowed limit (${maxSell}) based on ${gap}% gap interval for Gold`;
            }
          }
          return errors;
        }}
        onSubmit={handleSubmit}>
        {({ setFieldValue, isSubmitting }) => (
          <Form noValidate>
            <Card mt={8}>
              <Flex
                justifyContent='space-between'
                gap={5}>
                <SelectField
                  label='Select Location'
                  name='operationLocationId'
                  placeholder='Select Operation Location'
                  options={operationList.map((obj) => ({
                    value: obj.id,
                    label: obj.name,
                  }))}
                  required
                />

                <SelectField
                  label='type'
                  name='type'
                  options={MetalTypesOptions}
                  required
                  onChange={(e, field, form) => {
                    field.onChange(e);
                    const newType = e.target.value;
                    if (newType === 'GOLD22KT' && goldBuySellPriceGap !== null && form.values.buy) {
                      const buyPrice = Number(form.values.buy);
                      const gap = Number(goldBuySellPriceGap);
                      const maxSell = buyPrice - (buyPrice * gap / 100);
                      form.setFieldValue('sell', Math.round(maxSell));
                    }
                  }}
                />

                <InputField
                  label='Unit'
                  name='unit'
                  type='text'
                  placeholder='kg or gm'
                  required
                  disabled
                  variant='auth'
                />
              </Flex>

              <Flex
                direction={{ base: 'column', md: 'column', lg: 'row' }}
                justifyContent='space-between'
                gap={2}>
                <InputField
                  label='Start Date'
                  name='start'
                  required
                  placeholder='Start Date'
                  type='datetime-local'
                  max={filterEndDate} // Restrict to end date, whichever is earlier
                  value={filterStartDate}
                  onChange={(e) => {
                    setFieldValue('start', e.target.value);
                    setFilterStartDate(e.target.value);
                  }}
                />
                <InputField
                  label='End Date'
                  name='expiry'
                  required
                  placeholder='End Date'
                  type='datetime-local'
                  min={filterStartDate || today} // Ensure the end date is not before the start date
                  value={filterEndDate}
                  onChange={(e) => {
                    setFieldValue('expiry', e.target.value);
                    setFilterEndDate(e.target.value);
                  }}
                />
              </Flex>
            </Card>

            <Text
              mt={12}
              fontSize='xl'
              fontWeight='700'>
              Prices :
            </Text>
            <Card mt={4}>
              <Flex
                direction={{ base: 'column', md: 'column', lg: 'row' }}
                justifyContent='space-between'
                gap={2}>
                <InputField
                  label='Customer Buy price'
                  name='buy'
                  type='number'
                  placeholder='Enter Buy Value for Customer in Rs'
                  required
                  onChange={(e, field, form) => {
                    field.onChange(e);
                    if (form.values.type === 'GOLD22KT' && goldBuySellPriceGap !== null) {
                      const buyPrice = Number(e.target.value);
                      const gap = Number(goldBuySellPriceGap);
                      const maxSell = buyPrice - (buyPrice * gap / 100);
                      form.setFieldValue('sell', Math.round(maxSell));
                    }
                  }}
                />
                <InputField
                  label='Customer Sell price'
                  name='sell'
                  type='number'
                  placeholder='Enter Sell Value for Customer in Rs'
                  required
                />
                {/* <InputField
                  label='Customer Exchange price'
                  name='exchange'
                  type='number'
                  placeholder='Exchange'
                  required
                /> */}
              </Flex>
            </Card>

            <Text
              mt={12}
              fontSize='xl'
              fontWeight='700'>
              Locker Quantity :
            </Text>
            <Card mt={4}>
              <Flex
                direction={{ base: 'column', md: 'column', lg: 'row' }}
                justifyContent='space-between'
                gap={2}>
                <InputField
                  label='Customer Buy Quantity Threshold'
                  name='threshold_buy'
                  type='number'
                  placeholder='Enter Buy Quantity in gm'
                  required
                />
                <InputField
                  label='Customer Sell Quantity Threshold'
                  name='threshold_sell'
                  type='number'
                  placeholder='Enter Sell Quantity in gm'
                  required
                />

              </Flex>
              <Flex
                direction={{ base: 'column', md: 'column', lg: 'row' }}
                justifyContent='space-between'
                gap={2}>
                <InputField
                  label='Buy AddOn'
                  name='threshold_buy_addon'
                  type='number'
                  placeholder='Enter Buy AddOn Value In Rs'
                  required
                />
                <InputField
                  label='Sell Subtract'
                  name='threshold_sell_addon'
                  type='number'
                  placeholder='Enter Sell Subtract Value In Rs'
                  required
                />
              </Flex>
            </Card>

            <Flex>
              <Button
                mt={20}
                colorScheme='blue'
                type='submit'
                isLoading={isSubmitting}>
                Send For Approval
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
}
